import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { Refresh as RefreshIcon, Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { visitAPI, dispensingAPI, feeConfigAPI } from '../services/api';

// Dispensing item types map to fee-master categories for GST rates
const ITEM_TYPE_CATEGORY = {
  glasses: 'optical',
  'contact-lenses': 'optical',
  medicine: 'pharmacy',
  other: 'other',
};

const OPDPortal = () => {
  const [visits, setVisits] = useState([]);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [feeConfigs, setFeeConfigs] = useState([]);

  const [dispensingForm, setDispensingForm] = useState({
    items: [],
    prescribedMedicines: [],
    discount: 0,
    tax: 0,
    paymentMethod: 'cash',
    paidAmount: 0,
    followUpDate: '',
    followUpNotes: '',
    notes: '',
  });

  useEffect(() => {
    fetchWaitingPatients();
    feeConfigAPI.getFeeConfigs({ activeOnly: 'true' })
      .then((res) => setFeeConfigs(res.data.data))
      .catch(() => {});
  }, []);

  // Recompute the suggested tax from fee-master GST rates whenever items change
  useEffect(() => {
    const suggestedTax = dispensingForm.items.reduce((sum, item) => {
      const category = ITEM_TYPE_CATEGORY[item.itemType] || 'other';
      const cfg = feeConfigs.find((f) => f.category === category && (f.gstRate || 0) > 0)
        || feeConfigs.find((f) => f.category === category);
      return sum + (item.totalPrice || 0) * ((cfg?.gstRate || 0) / 100);
    }, 0);
    setDispensingForm((prev) => ({ ...prev, tax: Math.round(suggestedTax * 100) / 100 }));
  }, [dispensingForm.items, feeConfigs]);

  const fetchWaitingPatients = async () => {
    try {
      // Fetch patients waiting for OPD/dispensing (referred by reception or doctor)
      // Try both status values to catch all OPD patients
      const [dispensingResponse, opdResponse] = await Promise.all([
        visitAPI.getVisits({ status: 'waiting-dispensing' }).catch(() => ({ data: { data: [] } })),
        visitAPI.getVisits({ status: 'waiting-opd' }).catch(() => ({ data: { data: [] } }))
      ]);
      
      // Combine and deduplicate results
      const allVisits = [...dispensingResponse.data.data, ...opdResponse.data.data];
      const uniqueVisits = allVisits.filter((visit, index, self) =>
        index === self.findIndex((v) => v._id === visit._id)
      );
      
      setVisits(uniqueVisits);
    } catch (error) {
      showSnackbar('Failed to fetch patients', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleStartDispensing = async (visit) => {
    try {
      await visitAPI.startStage(visit._id, { stage: 'dispensing' });
      setSelectedVisit(visit);
      
      // Pre-populate prescribed medicines if available
      if (visit.prescriptions && visit.prescriptions.length > 0) {
        setDispensingForm(prev => ({
          ...prev,
          prescribedMedicines: visit.prescriptions
        }));
      }
      
      setOpenDialog(true);
      fetchWaitingPatients();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to start dispensing', 'error');
    }
  };

  const handleAddItem = () => {
    setDispensingForm({
      ...dispensingForm,
      items: [
        ...dispensingForm.items,
        { itemType: 'glasses', description: '', quantity: 1, unitPrice: 0, totalPrice: 0 },
      ],
    });
  };

  const handleRemoveItem = (index) => {
    const newItems = dispensingForm.items.filter((_, i) => i !== index);
    setDispensingForm({ ...dispensingForm, items: newItems });
  };

  const calculateTotal = () => {
    const subtotal = dispensingForm.items.reduce((sum, item) => sum + (item.totalPrice || 0), 0);
    const total = subtotal + dispensingForm.tax - dispensingForm.discount;
    return { subtotal, total };
  };

  const handleSaveDispensing = async () => {
    const { total } = calculateTotal();
    if (total < 0) {
      showSnackbar('Total cannot be negative — check discount/tax values', 'error');
      return;
    }
    if (dispensingForm.paidAmount < 0 || dispensingForm.paidAmount > total) {
      showSnackbar('Paid amount must be between 0 and the billed total', 'error');
      return;
    }
    try {
      // Save dispensing record
      await dispensingAPI.createRecord({
        visit: selectedVisit._id,
        ...dispensingForm,
        totalAmount: total,
      });
      
      // End dispensing stage
      await visitAPI.endStage(selectedVisit._id, { notes: 'OPD/Dispensing completed' });
      
      // Update visit status to completed
      await visitAPI.updateVisit(selectedVisit._id, {
        status: 'completed',
        followUpDate: dispensingForm.followUpDate || null
      });
      
      showSnackbar('OPD service completed successfully!');
      setOpenDialog(false);
      fetchWaitingPatients();
      resetForm();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to save record', 'error');
    }
  };

  const resetForm = () => {
    setDispensingForm({
      items: [],
      prescribedMedicines: [],
      discount: 0,
      tax: 0,
      paymentMethod: 'cash',
      paidAmount: 0,
      followUpDate: '',
      followUpNotes: '',
      notes: '',
    });
  };

  const { subtotal, total } = calculateTotal();

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" fontWeight="700">
          OPD Portal
        </Typography>
        <IconButton onClick={fetchWaitingPatients} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>

      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Token</TableCell>
                <TableCell>Patient</TableCell>
                <TableCell>Age / Gender</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Referred For</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visits.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">No patients waiting for OPD/dispensing</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                visits.map((visit) => (
                  <TableRow key={visit._id} hover>
                    <TableCell>
                      <Chip
                        label={`#${visit.tokenNumber || '-'}`}
                        color="primary"
                        size="small"
                        sx={{ fontWeight: 800, fontSize: '0.9rem', minWidth: 44 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{visit.patient?.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {visit.patient?.patientId} • {visit.visitId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{visit.patient?.age} yrs / {visit.patient?.gender}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{visit.patient?.contact?.phone || 'N/A'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="primary.main">
                        {visit.referralReason || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label="Waiting" color="warning" size="small" />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleStartDispensing(visit)}
                        sx={{ textTransform: 'none' }}
                      >
                        Start OPD Service
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* OPD/Dispensing Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          <Typography variant="h5" fontWeight="bold">
            OPD Service - {selectedVisit?.patient?.name}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
            Medicine Dispensing • Glasses/Contact Lens Sales • Follow-up Scheduling
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            
            {/* Prescribed Medicines Section */}
            {dispensingForm.prescribedMedicines.length > 0 && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                    Prescribed Medicines
                  </Typography>
                  <Table size="small" sx={{ mb: 2, bgcolor: 'info.lighter' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Medicine</strong></TableCell>
                        <TableCell><strong>Dosage</strong></TableCell>
                        <TableCell><strong>Frequency</strong></TableCell>
                        <TableCell><strong>Duration</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dispensingForm.prescribedMedicines.map((med, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{med.medicine?.name || 'N/A'}</TableCell>
                          <TableCell>{med.dosage}</TableCell>
                          <TableCell>{med.frequency}</TableCell>
                          <TableCell>{med.duration}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Items
                </Typography>
                <Button startIcon={<AddIcon />} onClick={handleAddItem}>
                  Add Item
                </Button>
              </Box>

              {dispensingForm.items.length > 0 && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Qty</TableCell>
                      <TableCell>Unit Price</TableCell>
                      <TableCell>Total</TableCell>
                      <TableCell width={50}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dispensingForm.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <TextField
                            select
                            size="small"
                            value={item.itemType}
                            onChange={(e) => {
                              const newItems = [...dispensingForm.items];
                              newItems[index].itemType = e.target.value;
                              setDispensingForm({ ...dispensingForm, items: newItems });
                            }}
                            SelectProps={{ native: true }}
                          >
                            <option value="glasses">Glasses</option>
                            <option value="contact-lenses">Contact Lenses</option>
                            <option value="medicine">Medicine</option>
                            <option value="other">Other</option>
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            value={item.description}
                            onChange={(e) => {
                              const newItems = [...dispensingForm.items];
                              newItems[index].description = e.target.value;
                              setDispensingForm({ ...dispensingForm, items: newItems });
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={item.quantity}
                            onChange={(e) => {
                              const newItems = [...dispensingForm.items];
                              newItems[index].quantity = parseInt(e.target.value) || 1;
                              newItems[index].totalPrice = newItems[index].quantity * newItems[index].unitPrice;
                              setDispensingForm({ ...dispensingForm, items: newItems });
                            }}
                            sx={{ width: 70 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={item.unitPrice}
                            onChange={(e) => {
                              const newItems = [...dispensingForm.items];
                              newItems[index].unitPrice = parseFloat(e.target.value) || 0;
                              newItems[index].totalPrice = newItems[index].quantity * newItems[index].unitPrice;
                              setDispensingForm({ ...dispensingForm, items: newItems });
                            }}
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                        <TableCell>₹{item.totalPrice.toFixed(2)}</TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => handleRemoveItem(index)}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Discount"
                type="number"
                value={dispensingForm.discount}
                onChange={(e) => setDispensingForm({ ...dispensingForm, discount: parseFloat(e.target.value) || 0 })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tax (auto from Fee Master)"
                type="number"
                value={dispensingForm.tax}
                onChange={(e) => setDispensingForm({ ...dispensingForm, tax: parseFloat(e.target.value) || 0 })}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body1">
                  <strong>Subtotal:</strong> ₹{subtotal.toFixed(2)}
                </Typography>
                <Typography variant="h6" color="primary">
                  <strong>Total:</strong> ₹{total.toFixed(2)}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Payment Method"
                value={dispensingForm.paymentMethod}
                onChange={(e) => setDispensingForm({ ...dispensingForm, paymentMethod: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="cash">Cash</option>
                <option value="card">Card</option>
                <option value="upi">UPI</option>
                <option value="insurance">Insurance</option>
                <option value="other">Other</option>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Paid Amount"
                type="number"
                required
                value={dispensingForm.paidAmount}
                onChange={(e) => setDispensingForm({ ...dispensingForm, paidAmount: parseFloat(e.target.value) || 0 })}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                Follow-up Appointment (Optional)
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Follow-up Date"
                type="date"
                value={dispensingForm.followUpDate}
                onChange={(e) => setDispensingForm({ ...dispensingForm, followUpDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Follow-up Notes"
                value={dispensingForm.followUpNotes}
                onChange={(e) => setDispensingForm({ ...dispensingForm, followUpNotes: e.target.value })}
                placeholder="Reason for follow-up..."
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Additional Notes"
                multiline
                rows={2}
                value={dispensingForm.notes}
                onChange={(e) => setDispensingForm({ ...dispensingForm, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveDispensing} variant="contained">
            Save & Complete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OPDPortal;
